/**
 * Dev codec — stands in for the Signal Double-Ratchet body (packages/crypto).
 *
 * In production `ciphertext` is an opaque base64url Double-Ratchet blob the
 * server never reads, and the crypto layer turns it into a plaintext payload.
 * Backendless, we base64url-encode a JSON envelope so the UI gets real wire
 * shapes (`WsStoredMessage.ciphertext` is a genuine base64url string) while the
 * "decrypt" step is a JSON parse. Swapping in real libsignal only changes these
 * two functions.
 */

import type { Base64Url, MessageContentType } from '@sinyal/contracts';

/** The plaintext that lives *inside* the encrypted body. */
export interface PlaintextBody {
  text: string;
  content_type: MessageContentType;
  forwarded?: boolean;
  /** Decrypted media descriptor (key + url), never server-visible. */
  media?: {
    media_id: string;
    mime: string;
    size: number;
    url: string;
    duration_sec?: number;
    width?: number;
    height?: number;
    file_name?: string;
  } | null;
}

function toBase64Url(s: string): string {
  // UTF-8 safe base64url.
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(b64: string): string {
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  const bin = atob(b64.replace(/-/g, '+').replace(/_/g, '/') + pad);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** "Encrypt" — JSON → base64url (real libsignal would Double-Ratchet here). */
export function seal(body: PlaintextBody): Base64Url {
  return toBase64Url(JSON.stringify(body));
}

/** "Decrypt" — base64url → JSON. */
export function open(ciphertext: Base64Url): PlaintextBody {
  return JSON.parse(fromBase64Url(ciphertext)) as PlaintextBody;
}
